import { useQuery } from "@tanstack/react-query";
import { HotelPage } from "~/applications/OfferSearch/Domain/HotelPage";
import { contentStudioClient } from "~/clients/ContentStudio";
import { localizedFieldsProjection } from "~/core/api/Sanity/localize";
import { buildGroqQuery } from "~/core/api/Sanity/query";
import { AVAILABLE_LOCALES } from "~/core/locale";
import { mapToHotelPageDomain } from "./hotelPage.mapper";
import { HotelPagesPayloadSchema } from "./hotelPagePayloadSchema";

const getHotelPageBySlugQuery = (locale: AVAILABLE_LOCALES) =>
  buildGroqQuery(`
  *[
    _type == 'logePage'
    && loge->destination._ref == $destinationId
    && seoSlug.current == $hotelSlug
    && !(_id in path('drafts.**'))
  ]{
    seoSlug{ current },
    gogaillePlacesAddressDetails{ ${localizedFieldsProjection(locale)} },
    gogaillePlacesDistancesDetails{ ${localizedFieldsProjection(locale)} },
    gogaillePlacesParkingsDetails{ ${localizedFieldsProjection(locale)} },
    accessibility {
      reducedMobilityRooms,
      elevator,
      stairliftPlatform
    },
    visibility,
    logo {
      asset->{
        url,
        metadata
      }
    },
    intro{ ${localizedFieldsProjection(locale)} },
    features {
      garden,
      terrace,
      bikePark,
      carPark
    },
    loge->{
      _id,
      pmsId{ current },
      name,
      gallery[] {
        asset->{
          url
        },
        caption
      },
      building->{
        address{
          line1,
          line2,
          postalCode,
          city,
          coordinate{ lat, lng }
        }
      }
    },
    houseKeeperEditorial {
      photo {
        asset->{
          url,
          metadata
        }
      },
      text{ ${localizedFieldsProjection(locale)} },
      additionalInformation{ ${localizedFieldsProjection(locale)} }
    },
    seoTitle{ ${localizedFieldsProjection(locale)} },
    seoDescription{ ${localizedFieldsProjection(locale)} },
    seoPhoto {
      asset->{
        url,
        metadata
      }
    }
  }
`);

type FetchHotelPageQuery = {
  destinationId: string;
  hotelSlug: string;
  locale: AVAILABLE_LOCALES;
};

export async function fetchHotelPage({
  destinationId,
  hotelSlug,
  locale,
}: FetchHotelPageQuery): Promise<HotelPage> {
  const response = await contentStudioClient.fetch(
    getHotelPageBySlugQuery(locale),
    { destinationId, hotelSlug },
  );

  const hotelPagesPayload = HotelPagesPayloadSchema.parse(response);

  if (hotelPagesPayload.length === 0) {
    throw new Error(
      `No hotel page with destination id ${destinationId} and hotel slug ${hotelSlug}`,
    );
  }

  const hotelPagePayloadItem = hotelPagesPayload[0];

  return mapToHotelPageDomain(hotelPagePayloadItem, locale);
}

export function useHotelPage(query: {
  destinationId: string;
  hotelSlug: string;
  locale: AVAILABLE_LOCALES;
}): { hotelPage: HotelPage | null } {
  const { data: hotelPage = null } = useQuery(
    ["offer-search-loge-page", query],
    () => fetchHotelPage(query),
  );

  return { hotelPage };
}
